import { ContentAnchor } from '@api/selections';
import { ContentLink, ImageConfig, LoadingStatus } from '@api/types';
import { LoadingSkeleton } from '@components';
import { Box, styled } from '@mui/material';
import { ImageResizerParams, getEpiImageResizerUrl, normalizeImageUrl } from '@utils/images.utils';
import classnames from 'classnames';
import { useMemo } from 'react';

type ImgProps = {
  height?: string;
  anchor?: ContentAnchor;
};

const Img = styled('img')<ImgProps>`
  object-fit: contain;
  object-position: center;
  &.fill {
    object-fit: cover;
    object-position: ${(props) => props.anchor};
    width: 100%;
    /* ${(props) => (props.height ? `height: ${props.height}px` : ``)}; */
    height: auto;
  }

  &.fit {
    object-fit: scale-down;
    width: 100%;
    height: 100%;
  }
  &.contain {
    object-fit: contain;
    width: 100%;
    height: 100%;
  }
  &.scale-down {
    object-fit: scale-down;
    width: 100%;
    height: 100%;
  }
`;

const Picture = styled('picture')`
  height: inherit;
  width: 100%;
`;

export type ImageProps = LoadingStatus &
  ImageConfig & {
    image?: ContentLink | string;
    alt?: string;
    title?: string;
    width?: string;
    height?: string;
    className?: string;
    addWrapper?: boolean;
    resizerParams?: ImageResizerParams;
    imgClassName?: string;
    sizes?: string;
    pictureSources?: { media?: string; params: ImageResizerParams }[];
  };

export const Image = ({
  image,
  title,
  alt,
  className,
  width,
  height,
  isLoading,
  scalingMode,
  anchor,
  addWrapper,
  resizerParams = {},
  imgClassName,
  sizes,
  pictureSources = [],
}: ImageProps) => {
  const fallbackUrl = useMemo(
    () => (image ? normalizeImageUrl(getEpiImageResizerUrl(image, resizerParams)) : ''),
    [image, resizerParams]
  );

  const sources = useMemo(
    () =>
      image
        ? pictureSources.map(({ media, params }, i) => ({
            key: i,
            media,
            url: normalizeImageUrl(getEpiImageResizerUrl(image, params)),
          }))
        : [],
    [image, pictureSources]
  );

  if (!image) return null;

  const mainClassName = classnames('epi-image-wrapper', className);

  const imageClassName = classnames('epi-image', scalingMode, anchor, imgClassName);

  const content = (
    <>
      {isLoading && (
        <LoadingSkeleton width={width} height={height} className='epi-image-skeleton' />
      )}
      {!isLoading &&
        (sources.length > 0 ? (
          <Picture>
            {sources.map(({ key, media, url }) =>
              media ? (
                <source key={key} media={media} srcSet={url} />
              ) : (
                <source key={key} srcSet={url} />
              )
            )}
            <Img
              src={fallbackUrl}
              loading='lazy'
              alt={alt}
              title={title}
              className={imageClassName}
              width={width}
              height={height}
              anchor={anchor}
              sizes={sizes}
            />
          </Picture>
        ) : (
          <Img
            src={fallbackUrl}
            loading='lazy'
            alt={alt}
            title={title}
            className={imageClassName}
            width={width}
            height={height}
            anchor={anchor}
            sizes={sizes}
          />
        ))}
    </>
  );

  return addWrapper ? (
    <Box className={mainClassName} sx={{ width: '100%', height }}>
      {content}
    </Box>
  ) : (
    content
  );
};
